'use client'
import React, { FC, ReactElement } from 'react'
import Input from '@/components/contained-input'
import classNames from 'classnames'
import featureFlags from '@/utils/feature-flags'
import { NETWORK } from '@/utils/constants'
import useFeeEstimates from '@/utils/hooks/useFeeEstimates'
import Slider from '@mui/material/Slider'
import styles from './FeeSlider.module.css'

const FeeSlider: FC<{ feeRate: number; setFeeRate: (v: any) => void; minFee?: number }> = (
	props
): ReactElement => {
	const { feeRate, setFeeRate } = props
	const [_showDetails, _setShowDetails] = React.useState(false)
	const [_fast, _setFast] = React.useState<number | null>(null)
	const [medium, setMedium] = React.useState<number | null>(null)
	const [slow, setSlow] = React.useState<number | null>(null)
	const [max, setMax] = React.useState<number | null>(null)
	const [minFeeRate, setMinFeeRate] = React.useState(5)

	const { data } = useFeeEstimates()

	React.useEffect(() => {
		requestAnimationFrame(() => {
			try {
				const minFee = parseInt((props?.minFee ? props?.minFee : data?.minimumFee)?.toFixed(0), 10)
				setSlow(data?.economyFee)
				setMedium(data?.halfHourFee)
				setMax(parseInt(data?.fastestFee?.toFixed(0), 10) * 2)
				setMinFeeRate(minFee)
				setFeeRate(data?.hourFee < minFee ? minFee : parseInt(data?.hourFee.toFixed(0), 10))
			} catch {
				const DEFAULT_FEE = 50
				const minFee = props?.minFee ? props.minFee : 10

				setSlow(5)
				setMedium(35)
				setMax(1000)
				setMinFeeRate(minFee)
				setFeeRate(DEFAULT_FEE < minFee ? minFee : DEFAULT_FEE)
			}
		})
	}, [data, props.minFee, setFeeRate])

	const handleChange = React.useCallback((_e: React.SyntheticEvent | Event, value: number | number[]) => {
		setFeeRate(value)
	}, [setFeeRate])

	const handleChangeInput = React.useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseFloat(evt.target.value)

		if (isNaN(value)) {
			return setFeeRate('')
		}

		if (!value || (NETWORK === 'BTC' && value > 1000)) {
			return
		}

		setFeeRate(value)
	}, [setFeeRate])

	if (featureFlags('app::disable_fee_slider')) {
		return <React.Fragment />
	}

	return (
		<div className={styles.root}>
			<div className={styles.network}>
				<div className={styles.header}>
					<div className={classNames(styles.title, 'text-md', 'bold')}>Network fee</div>
					<span className={classNames(styles.stats, 'text-sm', 'medium')}>
						{feeRate <= (slow ?? Infinity) && 'Slow'}
						{feeRate <= (medium ?? Infinity) && feeRate > (slow ?? Infinity) && 'Medium'}
						{feeRate > (medium ?? Infinity) && 'Fast'}
					</span>
				</div>
				<div className={styles.actions}>
					<Slider
						value={feeRate ? feeRate : 0}
						min={minFeeRate}
						max={max ?? undefined}
						step={1}
						onChange={handleChange}
						className={styles.slider}
						classes={{
							root: classNames(styles.slider)
						}}
					/>
					<Input
						type="number"
						min={minFeeRate?.toString()}
						max={max?.toString() ?? undefined}
						inputClassName={styles.input}
						className={styles.inputRoot}
						value={feeRate}
						onChange={handleChangeInput as any}
					/>
					<p className={classNames(styles.inputEndAdornmentLabel, 'text-xs', 'regular')}>
						sats/byte
					</p>
				</div>
			</div>
		</div>
	)
}

export default FeeSlider
